import jwtDecode from 'jwt-decode';
import { DeviceStatus as deviceStatus } from 'src/types/device_status';

const LABEL_PREFIX = 'modbus_v3_boiler_luna_mp_reg_';
// Namespaced so these popover keys cannot collide with the HIU `custom_popover_<nr>_*`
// keys, which reuse some of the same register numbers for different registers.
const POPOVER_PREFIX = 'custom_popover_boiler_luna_mp_';

// Namespaced per register, following the Remeha Effenca MT precedent. A single
// identifier grants both read and write on that register.
const PERMISSION_PREFIX = 'boiler-luna-mp-reg-';
const PERMISSION_SUFFIX = '-read';

// Roles live on the decoded access token rather than on the user object the callers pass in.
const isFortesSuperAdmin = (): boolean => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    const decoded: any = jwtDecode(token);
    return decoded?.realm_access?.roles?.includes('fortes_admin_super') ?? false;
  } catch {
    return false;
  }
};

// The `boiler-luna-mp-reg-<nr>-read` identifiers do not exist in Keycloak yet, so Fortes
// super admins are granted access unconditionally for now — without this every boiler
// register table and dashboard tile would render empty. Drop the super-admin branch once
// the permissions are created.
const hasRegisterPermission = (userPermissions: any, nr: string): boolean => (
  isFortesSuperAdmin()
  || userPermissions?.permissions?.includes(`${PERMISSION_PREFIX}${nr}${PERMISSION_SUFFIX}`)
);

// Resolves an enum register's option list, tolerating a missing translation or a
// translator that is not callable.
const parseEnumList = (translator: any, nr: string): any => {
  if (typeof translator !== 'function') return '';
  try {
    return JSON.parse(translator(`${LABEL_PREFIX}${nr}_label_list`))?.list ?? '';
  } catch {
    return '';
  }
};

// All four LUNA MP variants expose the same register set, so the definitions below are
// shared and the variant only selects the label shown in the device edit form.
const getBoilerLunaMPStatusByKey = (
  status_key: string,
  userPermissions: any = [],
  translator: any = {},
): Array<deviceStatus> => {
  let status: Array<deviceStatus>;

  const register = (
    nr: string,
    type: string,
    unit: string,
    category: string,
    scale: number,
    writable: boolean = false,
    listed: boolean = false,
  ): deviceStatus => ({
    nr,
    name: `${LABEL_PREFIX}${nr}_label`,
    category,
    isFrozen: false,
    value: '',
    viewable: true,
    type,
    // The translator returns the key itself when a translation is missing, and callers
    // sometimes pass a non-function default. Either way JSON.parse would throw and take
    // the whole register set down with it, leaving the caller with nothing.
    list: listed ? parseEnumList(translator, nr) : '',
    unit,
    scale,
    deviceListTableChecked: false,
    // Neither source mapping defines write ranges for these registers, so they are
    // left undefined — the write form skips its min/max check when they are absent.
    // A 0/0 pair here would reject every value as "must be between 0 and 0".
    min_value: undefined,
    max_value: undefined,
    read_permission: hasRegisterPermission(userPermissions, nr),
    write_permission: writable && hasRegisterPermission(userPermissions, nr),
    popoverKey: `${POPOVER_PREFIX}${nr}_long_description`,
    position: 0,
  });

  if (status_key === 'modbus_boiler_luna_mp_overview') {
    status = [
      // current_power
      register('413', 'float', '%', 'overview_current_power', 100),
      // flow_rate
      register('410', 'float', 'l/min', 'overview_flow_rate', 100),
      // water_pressure
      register('409', 'float', 'bar', 'overview_water_pressure', 10),
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_heating') {
    status = [
      // heat_demand
      register('416', 'float', '%', 'heating_heat_demand', 100),
      register('418', 'int', '', 'heating_heat_demand', 1),
      // setpoint
      register('406', 'float', '°C', 'heating_setpoint', 100),
      register('417', 'float', '°C', 'heating_setpoint', 100),
      register('458', 'float', '°C', 'heating_setpoint', 100), // placement/scale inferred - verify
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_domestic_hot_water') {
    status = [
      // setpoint
      register('405', 'float', '°C', 'domestic_hot_water_setpoint', 100),
      register('408', 'float', '°C', 'domestic_hot_water_setpoint', 100),
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_performance') {
    status = [
      // burner
      register('415', 'int', 'uA', 'performance_burner', 1), // placement/scale inferred - verify
      // burner_operating_hours
      register('421', 'int', 'h', 'performance_burner_operating_hours', 1),
      register('425', 'int', 'h', 'performance_burner_operating_hours', 1), // placement/scale inferred - verify
      register('429', 'int', 'h', 'performance_burner_operating_hours', 1), // placement/scale inferred - verify
      register('431', 'int', 'h', 'performance_burner_operating_hours', 1), // placement/scale inferred - verify
      // burner_starts
      register('419', 'int', '', 'performance_burner_starts', 1),
      register('423', 'int', '', 'performance_burner_starts', 1), // placement/scale inferred - verify
      register('427', 'int', '', 'performance_burner_starts', 1), // placement/scale inferred - verify
      // power
      register('414', 'float', '%', 'performance_power', 100),
      // pump_speed
      register('459', 'float', '%', 'performance_pump_speed', 100),
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_energy') {
    status = [
      // consumed_backup
      register('441', 'int', 'kW', 'energy_consumed_backup', 1), // placement/scale inferred - verify
      // consumed_central_heating
      register('433', 'int', 'kW', 'energy_consumed_central_heating', 1),
      // consumed_dhw
      register('435', 'int', 'kW', 'energy_consumed_dhw', 1),
      // consumed_total
      register('439', 'int', 'kW', 'energy_consumed_total', 1), // placement/scale inferred - verify
      // delivered_backup
      register('451', 'int', 'kW', 'energy_delivered_backup', 1), // placement/scale inferred - verify
      // delivered_central_heating
      register('445', 'int', 'kW', 'energy_delivered_central_heating', 1),
      // delivered_dhw
      register('447', 'int', 'kW', 'energy_delivered_dhw', 1),
      // delivered_total
      register('443', 'int', 'kW', 'energy_delivered_total', 1), // placement/scale inferred - verify
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_maintenance') {
    status = [
      // burner_starts
      register('516', 'int', '', 'maintenance_burner_starts', 1),
    ];
  } else if (status_key === 'modbus_boiler_luna_mp_configuration') {
    status = [
      // domestic_hot_water
      register('477', 'int', 'h', 'configuration_domestic_hot_water', 1, true),
      register('478', 'int', 'h', 'configuration_domestic_hot_water', 1, true),
      register('479', 'enum', '', 'configuration_domestic_hot_water', 1, true, true),
      // heating
      register('386', 'float', '°C', 'configuration_heating', 100, true),
      register('387', 'float', '°C', 'configuration_heating', 100, true), // placement/scale inferred - verify
      register('388', 'float', '°C', 'configuration_heating', 100, true),
      register('389', 'enum', '', 'configuration_heating', 1, true, true),
    ];
  } else {
    status = [];
  }

  return status;
};

// The four LUNA MP variants, as stored in `manufacturer_variant`. They all expose the
// same registers, so these are only ever used for labelling and filtering.
export const BOILER_LUNA_MP_1_35 = 'BOILER_LUNA_MP_1_35';
export const BOILER_LUNA_MP_1_50 = 'BOILER_LUNA_MP_1_50';
export const BOILER_LUNA_MP_1_60 = 'BOILER_LUNA_MP_1_60';
export const BOILER_LUNA_MP_1_70 = 'BOILER_LUNA_MP_1_70';

export const BOILER_LUNA_MP_VARIANTS = [
  BOILER_LUNA_MP_1_35,
  BOILER_LUNA_MP_1_50,
  BOILER_LUNA_MP_1_60,
  BOILER_LUNA_MP_1_70,
];

export const BOILER_LUNA_MP_VARIANT_LABELS = {
  [BOILER_LUNA_MP_1_35]: 'LUNA MP 1.35',
  [BOILER_LUNA_MP_1_50]: 'LUNA MP 1.50',
  [BOILER_LUNA_MP_1_60]: 'LUNA MP 1.60',
  [BOILER_LUNA_MP_1_70]: 'LUNA MP 1.70',
};

export const getBoilerLunaMPVariantLabel = (variant: any): string => (
  BOILER_LUNA_MP_VARIANT_LABELS[variant] ?? variant
);

// Reverse of the above: the filter input holds the readable name, but the query
// has to run against the key stored on the device.
export const getBoilerLunaMPVariantKey = (label: any): any => (
  BOILER_LUNA_MP_VARIANTS.find(
    (variant) => BOILER_LUNA_MP_VARIANT_LABELS[variant] === label
  ) ?? label
);

export default getBoilerLunaMPStatusByKey;
